import React, { useEffect, useMemo, useState } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Button,
    Modal,
    Tooltip,
    IconButton,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import BoltIcon from '@mui/icons-material/Bolt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import WalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';

import ApartmentCardEnergy from '../../components/ApartmentCardEnergy';
import DashboardApartmentCard from '../../components/dashboard/DashboardApartmentCard';
import {
    getAllProperties,
    getHousePeriodicConsumptions,
    getHouseBillingInfo,
} from '../../api_calls/api_calls_houses';
import { getVisitsForHouse } from '../../api_calls/api_calls_visits';
import {
    BUILDING_ADDRESS,
    getTotalBuildingConsumption,
    mapHouseToApartment,
    DEMO_TODAY,
} from '../../data/mockApartments';
import { useUserContext } from '../../context/UserContext';
import { useLanguageContext } from '../../hooks/useLanguageContext';
import { multilingual } from '../../multilingual';

const isOccupied = (checkIn, checkOut) => {
    const now = new Date(DEMO_TODAY);
    now.setHours(0, 0, 0, 0);
    const in_ = new Date(checkIn);
    in_.setHours(0, 0, 0, 0);
    const out = new Date(checkOut);
    out.setHours(23, 59, 59, 999);
    return now >= in_ && now <= out;
};

const SORT_OPTIONS = {
    number: 'number',
    highestConsumption: 'highestConsumption',
    lowestConsumption: 'lowestConsumption',
    highestCost: 'highestCost',
    lowestCost: 'lowestCost',
};

const ALL_BUILDINGS_KEY = 'ALL';
const GREEK_MONTHS_GENITIVE = [
    'ΙΑΝΟΥΑΡΙΟΥ',
    'ΦΕΒΡΟΥΑΡΙΟΥ',
    'ΜΑΡΤΙΟΥ',
    'ΑΠΡΙΛΙΟΥ',
    'ΜΑΪΟΥ',
    'ΙΟΥΝΙΟΥ',
    'ΙΟΥΛΙΟΥ',
    'ΑΥΓΟΥΣΤΟΥ',
    'ΣΕΠΤΕΜΒΡΙΟΥ',
    'ΟΚΤΩΒΡΙΟΥ',
    'ΝΟΕΜΒΡΙΟΥ',
    'ΔΕΚΕΜΒΡΙΟΥ',
];

const MyApartments = () => {
    const theme = useTheme();
    const { token } = useUserContext();

    const [houses, setHouses] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [loading, setLoading] = useState(true);

    const [energyModalOpen, setEnergyModalOpen] = useState(false);
    const [modalSortConsumption, setModalSortConsumption] = useState(''); // '' | 'highest' | 'lowest'
    const [modalApartmentData, setModalApartmentData] = useState({}); // { [apartmentId]: { lastWeekData, billingData } }
    const [modalDataLoading, setModalDataLoading] = useState(false);

    const [filterMode, setFilterMode] = useState('all'); // 'all' | 'occupied' | ...
    const [sortBy, setSortBy] = useState(SORT_OPTIONS.number);
    const [selectedFloor, setSelectedFloor] = useState('all');
    const [housesRefreshTick, setHousesRefreshTick] = useState(0);

    const { language } = useLanguageContext();
    const dictionary = multilingual[language]?.dashboard ?? multilingual.gr.dashboard;
    const apartmentCardDict = multilingual[language]?.apartmentCard ?? multilingual.gr.apartmentCard;

    const apartments = useMemo(() => houses.map(mapHouseToApartment), [houses]);

    const [selectedBuildingId, setSelectedBuildingId] = useState(ALL_BUILDINGS_KEY);

    useEffect(() => {
        if (!buildings.length) {
            setSelectedBuildingId(ALL_BUILDINGS_KEY);
            return;
        }
        setSelectedBuildingId((prev) => {
            if (prev === ALL_BUILDINGS_KEY) return prev;
            return buildings.some((b) => String(b?.id) === String(prev)) ? prev : ALL_BUILDINGS_KEY;
        });
    }, [buildings]);

    const displayedApartments = useMemo(() => {
        if (selectedBuildingId === ALL_BUILDINGS_KEY) return apartments;
        const selectedId = String(selectedBuildingId);
        return apartments.filter((a) => a?.buildingId != null && String(a.buildingId) === selectedId);
    }, [apartments, selectedBuildingId]);
    const floorList = useMemo(() => {
        const set = new Set();
        displayedApartments.forEach((a) => {
            if (a.floor != null && a.floor !== '') set.add(String(a.floor));
        });
        return Array.from(set).sort((a, b) => Number(a) - Number(b));
    }, [displayedApartments]);
    const apartmentsByFloor = useMemo(() => {
        if (selectedFloor === 'all') return displayedApartments;
        return displayedApartments.filter((a) => String(a.floor) === String(selectedFloor));
    }, [displayedApartments, selectedFloor]);

    useEffect(() => {
        setSelectedFloor((prev) => {
            if (prev === 'all') return prev;
            return floorList.includes(String(prev)) ? prev : 'all';
        });
    }, [floorList]);

    const selectedBuilding = useMemo(() => {
        if (selectedBuildingId === ALL_BUILDINGS_KEY) return null;
        return buildings.find((b) => String(b?.id) === String(selectedBuildingId)) || null;
    }, [buildings, selectedBuildingId]);
    const isAllBuildingsSelected = selectedBuildingId === ALL_BUILDINGS_KEY;

    const totalConsumption = getTotalBuildingConsumption(displayedApartments);
    const totalDisplayedCost = useMemo(
        () =>
            displayedApartments.reduce((sum, apartment) => {
                const cost = apartment?.billingCost;
                return sum + (cost != null ? Number(cost) || 0 : 0);
            }, 0),
        [displayedApartments],
    );
    const periodIndicatorLabel = useMemo(() => {
        const now = new Date();
        const isGreek = String(language || '').toLowerCase().startsWith('gr');
        if (isGreek) {
            const month = GREEK_MONTHS_GENITIVE[now.getMonth()] || '';
            return `ΑΠΟ 1Η ${month}`;
        }
        const month = now.toLocaleString('en-US', { month: 'long' }).toUpperCase();
        return `SINCE ${month} 1ST`;
    }, [language]);
    const buildingAddress =
        selectedBuilding?.address ||
        selectedBuilding?.name ||
        displayedApartments[0]?.address ||
        displayedApartments[0]?.name ||
        BUILDING_ADDRESS;
    const currentSelectionTitle = isAllBuildingsSelected
        ? dictionary.allManagedProperties
        : (selectedBuilding?.address || selectedBuilding?.name || buildingAddress);
    const selectedSwitcherLabel = isAllBuildingsSelected
        ? dictionary.allProperties
        : (selectedBuilding?.name || selectedBuilding?.address || '');
    const selectedBuildingName = selectedBuilding?.name || '—';
    const selectedBuildingAddress = selectedBuilding?.address || '—';

    const apartmentIdsKey = useMemo(
        () => displayedApartments.map((a) => a.id).sort().join(','),
        [displayedApartments],
    );

    useEffect(() => {
        if (!token) {
            setHouses([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        getAllProperties(token)
            .then((data) => {
                const isArrayResponse = Array.isArray(data);
                const isTopLevelBuildingList =
                    isArrayResponse && data.some((item) => Array.isArray(item?.houses));
                const buildingsData = Array.isArray(data?.buildings)
                    ? data.buildings
                    : isTopLevelBuildingList
                        ? data
                        : [];
                const housesFromBuildings = buildingsData.flatMap((building) => {
                    const scopedHouses = Array.isArray(building?.houses) ? building.houses : [];
                    return scopedHouses.map((house) => ({
                        ...house,
                        buildingId: building?.id ?? null,
                        buildingName: building?.name ?? '',
                        buildingAddress: building?.address ?? house?.address ?? '',
                    }));
                });
                // Canonical house -> building mapping from nested buildings payload.
                const houseBuildingById = new Map();
                housesFromBuildings.forEach((house) => {
                    const idKey = house?.id != null ? String(house.id) : '';
                    if (!idKey) return;
                    houseBuildingById.set(idKey, {
                        buildingId: house?.buildingId != null ? String(house.buildingId) : null,
                        buildingName: house?.buildingName || '',
                        buildingAddress: house?.buildingAddress || house?.address || '',
                    });
                });
                const housesTopLevel = Array.isArray(data?.houses)
                    ? data.houses
                    : isArrayResponse && !isTopLevelBuildingList
                        ? data
                        : [];
                const merged = [...housesFromBuildings, ...housesTopLevel];
                const byKey = new Map();
                merged.forEach((house) => {
                    const key = house?.id || `${house?.name || ''}-${house?.address || ''}-${house?.floor || ''}`;
                    const idKey = house?.id != null ? String(house.id) : '';
                    const mapped = idKey ? houseBuildingById.get(idKey) : null;
                    const normalizedBuildingId =
                        house?.buildingId != null && String(house.buildingId) !== ''
                            ? String(house.buildingId)
                            : mapped?.buildingId ?? null;
                    const normalizedBuildingName =
                        house?.buildingName && String(house.buildingName).trim() !== ''
                            ? house.buildingName
                            : mapped?.buildingName ?? '';
                    const normalizedBuildingAddress =
                        house?.buildingAddress && String(house.buildingAddress).trim() !== ''
                            ? house.buildingAddress
                            : mapped?.buildingAddress ?? house?.address ?? '';
                    const normalizedHouse = {
                        ...house,
                        buildingId: normalizedBuildingId,
                        buildingName: normalizedBuildingName,
                        buildingAddress: normalizedBuildingAddress,
                    };
                    const previous = byKey.get(key);
                    if (!previous) {
                        byKey.set(key, normalizedHouse);
                        return;
                    }
                    byKey.set(key, {
                        ...previous,
                        ...normalizedHouse,
                        // Preserve building linkage when top-level duplicates do not include it.
                        buildingId:
                            normalizedHouse?.buildingId != null && String(normalizedHouse.buildingId) !== ''
                                ? String(normalizedHouse.buildingId)
                                : previous?.buildingId ?? null,
                        buildingName:
                            normalizedHouse?.buildingName && String(normalizedHouse.buildingName).trim() !== ''
                                ? normalizedHouse.buildingName
                                : previous?.buildingName ?? '',
                        buildingAddress:
                            normalizedHouse?.buildingAddress && String(normalizedHouse.buildingAddress).trim() !== ''
                                ? normalizedHouse.buildingAddress
                                : previous?.buildingAddress ?? normalizedHouse?.address ?? previous?.address ?? '',
                    });
                });
                const dedupedById = Array.from(byKey.values());
                if (dedupedById.length === 0) {
                    setBuildings(buildingsData);
                    setHouses([]);
                    return;
                }

                return Promise.all(
                    dedupedById.map((house) =>
                        Promise.all([getHousePeriodicConsumptions('lastweek', house.id), getHouseBillingInfo(house.id)])
                            .then(([periodic, billing]) => {
                                const consumption = Array.isArray(periodic?.consumption) ? periodic.consumption : [];
                                const totalConsumption =
                                    consumption.reduce((s, v) => s + (Number(v) || 0), 0) || Number(house.totalConsumption) || 0;
                                const billingCost = billing?.cost ?? billing?.total ?? billing?.billing_cost;
                                return {
                                    ...house,
                                    totalConsumption,
                                    billingCost: billingCost != null ? Number(billingCost) : house?.billingCost,
                                };
                            })
                            .catch(() => ({
                                ...house,
                                totalConsumption: Number(house.totalConsumption) || 0,
                                billingCost: house?.billingCost,
                            })),
                    ),
                ).then((enrichedHouses) => {
                    setBuildings(buildingsData);
                    setHouses(enrichedHouses);
                });
            })
            .catch(() => {
                setBuildings([]);
                setHouses([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [token, housesRefreshTick]);

    useEffect(() => {
        if (!energyModalOpen || !displayedApartments.length) {
            if (!energyModalOpen) setModalApartmentData({});
            return;
        }
        let mounted = true;
        setModalDataLoading(true);
        Promise.all(
            displayedApartments.map((apt) =>
                Promise.all([getHousePeriodicConsumptions('lastweek', apt.id), getHouseBillingInfo(apt.id)]).then(
                    ([periodic, billing]) => {
                        const consumption = Array.isArray(periodic?.consumption) ? periodic.consumption : [];
                        const labels = Array.isArray(periodic?.labels) ? periodic.labels : [];
                        const total = consumption.reduce((sum, v) => sum + (Number(v) || 0), 0);
                        return {
                            id: apt.id,
                            lastWeekData: { consumption, labels, total },
                            billingData: billing || null,
                        };
                    },
                ),
            ),
        )
            .then((results) => {
                if (!mounted) return;
                const byId = {};
                results.forEach((r) => {
                    byId[r.id] = { lastWeekData: r.lastWeekData, billingData: r.billingData };
                });
                setModalApartmentData(byId);
            })
            .catch(() => {
                if (!mounted) return;
                setModalApartmentData({});
            })
            .finally(() => {
                if (mounted) setModalDataLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, [energyModalOpen, apartmentIdsKey, displayedApartments]);

    useEffect(() => {
        if (!houses.length) return;
        let mounted = true;
        Promise.all(
            houses.map((house) =>
                getVisitsForHouse(house.id)
                    .then((visits) => ({ house, visits }))
                    .catch(() => ({ house, visits: [] })),
            ),
        ).then((results) => {
            if (!mounted) return;
            results.forEach(({ house, visits }) => {
                if (Array.isArray(visits) && visits.length > 0) {
                    console.log(`[Visits] House with visits: ${house?.name || house?.id}`);
                }
            });
        });
        return () => {
            mounted = false;
        };
    }, [houses]);

    if (loading) {
        return (
            <Container
                maxWidth="lg"
                sx={{
                    my: 5,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 300,
                }}
            >
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ my: 5, '@media (min-width: 1920px)': { maxWidth: 1536 } }}>
            <Grid container spacing={2}>
                <Paper
                    elevation={0}
                    sx={{
                        px: 2.5,
                        py: 2,
                        borderRadius: 4,
                        backgroundColor: theme.palette.common.white,
                        width: '100%',
                        mb: 3,
                        boxShadow: 'none',
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                        {(buildings.length > 0 || apartments.length > 0) && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    p: 0.75,
                                    borderRadius: 999,
                                    bgcolor: '#e5e7eb',
                                    overflowX: 'auto',
                                }}
                            >
                                <Button
                                    size="small"
                                    onClick={() => setSelectedBuildingId(ALL_BUILDINGS_KEY)}
                                    sx={{
                                        textTransform: 'uppercase',
                                        px: 2,
                                        py: 0.9,
                                        borderRadius: 999,
                                        whiteSpace: 'nowrap',
                                        fontWeight: 700,
                                        letterSpacing: 0.2,
                                        bgcolor: selectedBuildingId === ALL_BUILDINGS_KEY ? '#fff' : 'transparent',
                                        color: selectedBuildingId === ALL_BUILDINGS_KEY ? '#3b82f6' : '#4b5563',
                                        boxShadow: selectedBuildingId === ALL_BUILDINGS_KEY ? '0 1px 2px rgba(15, 23, 42, 0.15)' : 'none',
                                        '&:hover': { bgcolor: selectedBuildingId === ALL_BUILDINGS_KEY ? '#fff' : 'rgba(255,255,255,0.45)' },
                                    }}
                                >
                                    {dictionary.allProperties}
                                </Button>
                                {buildings.map((building) => {
                                    const active = String(selectedBuildingId) === String(building.id);
                                    return (
                                        <Button
                                            key={building.id}
                                            size="small"
                                            onClick={() => setSelectedBuildingId(String(building.id))}
                                            sx={{
                                                textTransform: 'uppercase',
                                                px: 2,
                                                py: 0.9,
                                                borderRadius: 999,
                                                whiteSpace: 'nowrap',
                                                fontWeight: 700,
                                                letterSpacing: 0.2,
                                                bgcolor: active ? '#fff' : 'transparent',
                                                color: active ? '#3b82f6' : '#4b5563',
                                                boxShadow: active ? '0 1px 2px rgba(15, 23, 42, 0.15)' : 'none',
                                                '&:hover': { bgcolor: active ? '#fff' : 'rgba(255,255,255,0.45)' },
                                            }}
                                        >
                                            {building?.name || building?.address || 'Building'}
                                        </Button>
                                    );
                                })}
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', mt: 2, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        color: '#6b7280',
                                        letterSpacing: '0.08em',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    <LocationOnIcon sx={{ fontSize: 15 }} />
                                    {dictionary.currentSelectionLabel}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.5, flexWrap: 'wrap' }}>
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: 800, color: '#111827', display: 'inline-flex', alignItems: 'center', gap: 0.8 }}
                                    >
                                        <HomeIcon sx={{ fontSize: 24, color: '#2563eb' }} />
                                        {isAllBuildingsSelected ? currentSelectionTitle : selectedBuildingName}
                                    </Typography>
                                    {!isAllBuildingsSelected && (
                                        <Chip
                                            size="small"
                                            icon={<LocationOnIcon sx={{ fontSize: 16 }} />}
                                            label={selectedBuildingAddress}
                                            sx={{
                                                border: '1px solid #93c5fd',
                                                bgcolor: '#dbeafe',
                                                color: '#1e3a8a',
                                                fontWeight: 600,
                                                '& .MuiChip-icon': { color: '#2563eb' },
                                            }}
                                        />
                                    )}
                                    <Chip
                                        size="small"
                                        label={
                                            isAllBuildingsSelected
                                                ? dictionary.summaryBadge
                                                : dictionary.activeBadge
                                        }
                                        sx={{
                                            height: 22,
                                            bgcolor: '#dbeafe',
                                            color: '#1d4ed8',
                                            borderRadius: 1,
                                            fontWeight: 700,
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Box
                                    sx={{
                                        p: 2.25,
                                        pb: 5,
                                        borderRadius: 2,
                                        bgcolor: '#fff',
                                        border: '1px solid #e2e8f0',
                                        borderTop: '4px solid #2563eb',
                                        height: '100%',
                                        position: 'relative',
                                    }}
                                >
                                    <Box sx={{ width: 38, height: 38, borderRadius: 1.5, bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.2 }}>
                                        <ApartmentOutlinedIcon sx={{ color: '#2563eb' }} />
                                    </Box>
                                    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700 }}>
                                        {dictionary.apartmentsCount}
                                    </Typography>
                                    <Typography variant="h4" sx={{ mt: 0.3, fontWeight: 800, color: '#111827' }}>
                                        {displayedApartments.length}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                        {isAllBuildingsSelected
                                            ? dictionary.apartmentsCountDescriptionAll
                                            : dictionary.apartmentsCountDescriptionSelected}
                                    </Typography>
                                    <Chip
                                        label={periodIndicatorLabel}
                                        sx={{
                                            position: 'absolute',
                                            left: 18,
                                            bottom: 14,
                                            height: 20,
                                            borderRadius: 1,
                                            bgcolor: '#eff6ff',
                                            border: '1px solid #bfdbfe',
                                            color: '#2563eb',
                                            '& .MuiChip-label': {
                                                px: 0.9,
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.2,
                                            },
                                        }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box
                                    sx={{
                                        p: 2.25,
                                        pb: 5,
                                        borderRadius: 2,
                                        bgcolor: '#fff',
                                        border: '1px solid #e2e8f0',
                                        borderTop: '4px solid #c2410c',
                                        height: '100%',
                                        position: 'relative',
                                    }}
                                >
                                    <Box sx={{ width: 38, height: 38, borderRadius: 1.5, bgcolor: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.2 }}>
                                        <BoltIcon sx={{ color: '#c2410c' }} />
                                    </Box>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700 }}>
                                            {dictionary.totalConsumption}
                                        </Typography>
                                        <Tooltip title={dictionary.refersToLastMonth} placement="top" arrow>
                                            <IconButton size="small" sx={{ p: 0, color: '#6b7280' }}>
                                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Typography variant="h4" sx={{ mt: 0.3, fontWeight: 800, color: '#111827' }}>
                                        {totalConsumption.toFixed(2)} kWh
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                        {isAllBuildingsSelected
                                            ? dictionary.consumptionDescriptionAll
                                            : dictionary.consumptionDescriptionSelected}
                                    </Typography>
                                    <Chip
                                        label={periodIndicatorLabel}
                                        sx={{
                                            position: 'absolute',
                                            left: 18,
                                            bottom: 14,
                                            height: 20,
                                            borderRadius: 1,
                                            bgcolor: '#fff7ed',
                                            border: '1px solid #fed7aa',
                                            color: '#c2410c',
                                            '& .MuiChip-label': {
                                                px: 0.9,
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.2,
                                            },
                                        }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box
                                    sx={{
                                        p: 2.25,
                                        pb: 5,
                                        borderRadius: 2,
                                        bgcolor: '#fff',
                                        border: '1px solid #e2e8f0',
                                        borderTop: '4px solid #0f766e',
                                        height: '100%',
                                        position: 'relative',
                                    }}
                                >
                                    <Box sx={{ width: 38, height: 38, borderRadius: 1.5, bgcolor: '#ecfeff', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.2 }}>
                                        <WalletOutlinedIcon sx={{ color: '#0f766e' }} />
                                    </Box>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700 }}>
                                            {dictionary.estimatedCost}
                                        </Typography>
                                        <Tooltip title={dictionary.refersToLastMonth} placement="top" arrow>
                                            <IconButton size="small" sx={{ p: 0, color: '#6b7280' }}>
                                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Typography variant="h4" sx={{ mt: 0.3, fontWeight: 800, color: '#111827' }}>
                                        € {totalDisplayedCost.toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                        {isAllBuildingsSelected
                                            ? dictionary.estimatedCostDescriptionAll
                                            : dictionary.estimatedCostDescriptionSelected}
                                    </Typography>
                                    <Chip
                                        label={periodIndicatorLabel}
                                        sx={{
                                            position: 'absolute',
                                            left: 18,
                                            bottom: 14,
                                            height: 20,
                                            borderRadius: 1,
                                            bgcolor: '#ecfeff',
                                            border: '1px solid #99f6e4',
                                            color: '#0f766e',
                                            '& .MuiChip-label': {
                                                px: 0.9,
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.2,
                                            },
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>

            {displayedApartments.length === 0 && (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography color="text.secondary">{dictionary.apartments} — no data</Typography>
                </Paper>
            )}

            {displayedApartments.length > 0 && (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, width: '100%' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.35, flexShrink: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="h6" fontWeight={700} color="text.primary">
                                        {dictionary.apartments}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={selectedSwitcherLabel}
                                        sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }}
                                    />
                                </Box>
                                <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', fontWeight: 600 }}>
                                    {dictionary.apartmentsListSubtitle}
                                </Typography>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1.5,
                                flexWrap: 'wrap',
                                bgcolor: '#f3f4f6',
                                borderRadius: 3,
                                p: 1.25,
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: '100%', md: 'auto' },
                                    minWidth: 0,
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        bgcolor: '#e5e7eb',
                                        borderRadius: 999,
                                        p: 0.4,
                                        gap: 0,
                                        minWidth: { xs: '100%', sm: 320 },
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    <Button
                                        size="small"
                                        onClick={() => setSelectedFloor('all')}
                                        sx={{
                                            textTransform: 'none',
                                            minWidth: 0,
                                            px: 2,
                                            py: 0.75,
                                            borderRadius: 999,
                                            bgcolor: selectedFloor === 'all' ? '#fff' : 'transparent',
                                            color: selectedFloor === 'all' ? 'primary.main' : 'grey.700',
                                            boxShadow: selectedFloor === 'all' ? 1 : 'none',
                                            '&:hover': { bgcolor: selectedFloor === 'all' ? '#fff' : 'rgba(255,255,255,0.5)' },
                                        }}
                                    >
                                        {dictionary.floorAll}
                                    </Button>
                                    {floorList.map((floorKey) => (
                                        <Button
                                            key={floorKey}
                                            size="small"
                                            onClick={() => setSelectedFloor(floorKey)}
                                            sx={{
                                                textTransform: 'none',
                                                minWidth: 0,
                                                px: 2,
                                                py: 0.75,
                                                borderRadius: 999,
                                                bgcolor: selectedFloor === floorKey ? '#fff' : 'transparent',
                                                color: selectedFloor === floorKey ? 'primary.main' : 'grey.700',
                                                boxShadow: selectedFloor === floorKey ? 1 : 'none',
                                                '&:hover': { bgcolor: selectedFloor === floorKey ? '#fff' : 'rgba(255,255,255,0.5)' },
                                            }}
                                        >
                                            {dictionary.floor} {floorKey}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto' }}>
                                <Button
                                    size="small"
                                    onClick={() => setFilterMode((prev) => (prev === 'occupied' ? 'all' : 'occupied'))}
                                    startIcon={
                                        filterMode === 'occupied' ? (
                                            <CheckBoxOutlinedIcon sx={{ color: '#2563eb' }} />
                                        ) : (
                                            <CheckBoxOutlineBlankOutlinedIcon sx={{ color: '#64748b' }} />
                                        )
                                    }
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        bgcolor: '#fff',
                                        color: '#1f2937',
                                        border: '1px solid #d1d5db',
                                        px: 1.25,
                                        py: 0.65,
                                    }}
                                >
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2563eb', mr: 1 }} />
                                    {dictionary.filterOccupied}
                                </Button>
                                <FormControl size="small" sx={{ minWidth: 180, bgcolor: '#fff', borderRadius: 2 }}>
                                    <InputLabel id="sort-label">{dictionary.sortBy}</InputLabel>
                                    <Select
                                        labelId="sort-label"
                                        value={sortBy || SORT_OPTIONS.number}
                                        label={dictionary.sortBy}
                                        onChange={(e) => setSortBy(String(e.target.value ?? SORT_OPTIONS.number))}
                                    >
                                        <MenuItem value={SORT_OPTIONS.number}>{dictionary.sortByNumber}</MenuItem>
                                        <MenuItem value={SORT_OPTIONS.highestConsumption}>{dictionary.sortByHighestConsumption}</MenuItem>
                                        <MenuItem value={SORT_OPTIONS.lowestConsumption}>{dictionary.sortByLowestConsumption}</MenuItem>
                                        <MenuItem value={SORT_OPTIONS.highestCost}>{dictionary.sortByHighestCost}</MenuItem>
                                        <MenuItem value={SORT_OPTIONS.lowestCost}>{dictionary.sortByLowestCost}</MenuItem>
                                    </Select>
                                </FormControl>
                                <Button
                                    variant="contained"
                                    startIcon={<BoltOutlinedIcon sx={{ fontSize: 18 }} />}
                                    onClick={() => setEnergyModalOpen(true)}
                                    sx={{
                                        textTransform: 'none',
                                        px: 2,
                                        py: 0.65,
                                        bgcolor: '#2563eb',
                                        color: '#fff',
                                        boxShadow: 1,
                                        '&:hover': { bgcolor: '#1d4ed8' },
                                    }}
                                >
                                    {dictionary.energyOverviewButton}
                                </Button>
                            </Box>
                        </Box>
                    </Box>

                    <Grid container spacing={2} sx={{ width: '100%', maxWidth: '100%' }}>
                        {(() => {
                            let list = [...apartmentsByFloor];
                            if (filterMode === 'occupied') {
                                list = list.filter((a) => isOccupied(a.checkIn, a.checkOut));
                            }
                            const getConsumption = (a) => Number(a.totalConsumption) || 0;
                            const getCost = (a) => (a.billingCost != null ? Number(a.billingCost) : null) ?? getConsumption(a) * 0.16;
                            const sortKey = String(sortBy);
                            if (sortKey === SORT_OPTIONS.highestConsumption) {
                                list = list.slice().sort((a, b) => getConsumption(b) - getConsumption(a));
                            } else if (sortKey === SORT_OPTIONS.lowestConsumption) {
                                list = list.slice().sort((a, b) => getConsumption(a) - getConsumption(b));
                            } else if (sortKey === SORT_OPTIONS.highestCost) {
                                list = list.slice().sort((a, b) => getCost(b) - getCost(a));
                            } else if (sortKey === SORT_OPTIONS.lowestCost) {
                                list = list.slice().sort((a, b) => getCost(a) - getCost(b));
                            } else {
                                list = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                            }

                            const dateLocale = language === 'gr' ? 'el-GR' : 'en-GB';

                            return list.map((apartment) => (
                                <Grid item xs={12} key={apartment.id} sx={{ width: '100%' }}>
                                    <DashboardApartmentCard
                                        apartment={apartment}
                                        dictionary={dictionary}
                                        apartmentCardDict={apartmentCardDict}
                                        dateLocale={dateLocale}
                                        buildingAddress={apartment.buildingAddress || apartment.address || buildingAddress}
                                        buildingId={apartment.buildingId}
                                        onHouseUpdated={() => setHousesRefreshTick((prev) => prev + 1)}
                                    />
                                </Grid>
                            ));
                        })()}
                    </Grid>
                </>
            )}

            <Modal
                open={energyModalOpen}
                onClose={() => setEnergyModalOpen(false)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
            >
                <Paper
                    elevation={8}
                    sx={{
                        p: 3,
                        maxWidth: 1200,
                        width: '100%',
                        maxHeight: 'calc(100vh - 64px)',
                        overflow: 'auto',
                    }}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                        <Typography variant="h6" fontWeight={600}>
                            {dictionary.apartments}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="modal-sort-label">{dictionary.sortBy}</InputLabel>
                            <Select
                                labelId="modal-sort-label"
                                value={modalSortConsumption}
                                label={dictionary.sortBy}
                                onChange={(e) => setModalSortConsumption(e.target.value)}
                            >
                                <MenuItem value="">—</MenuItem>
                                <MenuItem value="highest">{dictionary.sortByHighestConsumption}</MenuItem>
                                <MenuItem value="lowest">{dictionary.sortByLowestConsumption}</MenuItem>
                                <MenuItem value="highestCost">{dictionary.sortByHighestCost}</MenuItem>
                                <MenuItem value="lowestCost">{dictionary.sortByLowestCost}</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Grid container spacing={2}>
                        {modalDataLoading ? (
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Grid>
                        ) : (
                            [...displayedApartments]
                                .sort((a, b) => {
                                    const totalA = modalApartmentData[a.id]?.lastWeekData?.total ?? a.totalConsumption ?? 0;
                                    const totalB = modalApartmentData[b.id]?.lastWeekData?.total ?? b.totalConsumption ?? 0;
                                    const getCost = (apt) => {
                                        const data = modalApartmentData[apt.id];
                                        const billing = data?.billingData;
                                        const cost = billing?.cost ?? billing?.total ?? billing?.billing_cost ?? apt.billingCost;
                                        if (cost != null) return Number(cost);
                                        return (data?.lastWeekData?.total ?? apt.totalConsumption ?? 0) * 0.16;
                                    };
                                    if (modalSortConsumption === 'highest') return totalB - totalA;
                                    if (modalSortConsumption === 'lowest') return totalA - totalB;
                                    if (modalSortConsumption === 'highestCost') return getCost(b) - getCost(a);
                                    if (modalSortConsumption === 'lowestCost') return getCost(a) - getCost(b);
                                    return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
                                })
                                .map((apartment) => (
                                    <Grid item xs={12} sm={6} md={4} key={apartment.id}>
                                        <ApartmentCardEnergy apartment={apartment} inModal initialData={modalApartmentData[apartment.id]} />
                                    </Grid>
                                ))
                        )}
                    </Grid>
                </Paper>
            </Modal>
        </Container>
    );
};

export default MyApartments;
